import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

import { CmsPage, CmsPageSection } from '../../models/content';
import { FileService } from '../../services/file.service';
import { ModalService } from '../../services/modal.service';
import { AccountService } from '../../services/account.service';
import { first, switchMap } from 'rxjs/operators';
import { faqOptionsFrom } from './faq-options';
import {
  BLOG_VARIANT_OPTIONS,
  FAQ_VARIANT_OPTIONS,
  GALLERY_VARIANT_OPTIONS,
  ALIGN_OPTIONS,
  IMAGE_LAYOUT_OPTIONS,
  LINK_STYLE_OPTIONS,
  PANEL_COLUMN_OPTIONS,
  PANEL_ROW_OPTIONS,
  SUBURB_PLACES_OPTIONS,
  VARIANT_OPTIONS,
  WIDE_COLUMN_OPTIONS,
} from './content-sections-options';
import { SUBREGION_BLOCKS_LIVE, SUBURB_TEMPLATE_BLOCKS_LIVE } from './suburbs/content/suburb-page';
import { insertToken, withTargetToken } from './suburbs/template/suburb-template';
import {
  BIND_OPTIONS,
  canMove,
  componentGroupsFor,
  isRemovable,
  SectionsEditorMode,
} from './suburbs/template/template-blocks';

import { BACKGROUND_OPTIONS, TEXT_COLOUR_OPTIONS, inheritedPlaceholder } from './block-colours';
import { EditablePanel, toStoredPanel } from './cms-panel';

/**
 * The building-block (sections) editor, extracted from the page editor so a
 * page's layout can be edited from MORE than one door — the Pages editor and
 * the Region editor's "Page layout" tab both embed this (Ivan's "everything
 * in one place", 2026-08-21).
 *
 * Owns the working section list, every per-section field helper, uploads and
 * the serialization back to the id-keyed `_meta.index` map. The HOST owns
 * page-level concerns (path, title, meta, publish) and calls:
 *
 *   [mode]               — 'page' (default), or a suburb page template / one suburb's inserted block (#262;
 *                          the rules are suburbs/template/template-blocks.ts)
 *   initFrom(raw)        — load from a CmsPage's stored sections
 *   addSection(type)     — from the host's action bar
 *   validate(published)  — section-level rules; string = the problem
 *   toMap()              — the id-keyed map for patchContentItem
 *   (edited)             — something may have changed (typing, a choice, an upload attached): the block preview (#263)
 *   activeSectionId      — the block last focused or clicked, and (activeChange) when it changes
 */

/** Pickers whose panels PrimeNG appends to <body>: a choice made there never reaches this component's host. */
const OVERLAY_PANELS = '.p-dropdown-panel, .p-multiselect-panel, .p-autocomplete-panel, .p-menu-overlay';

/** Working copy of a section while the dialog is open. */
export interface EditableSection extends Omit<CmsPageSection, '_meta'> {
  id: string;
  /** Signed preview URL for an uploaded image (resolved lazily). */
  heroPreview?: string | null;
  /** Comma-separated view of `services` for the input field. */
  servicesCsv?: string;
  /** Card collapsed in the editor (pages carry heaps of blocks). Transient. */
  collapsed?: boolean;
  /** gallery entries with a transient preview link (stripped in toMap). */
  images?: Array<{ fileId: number; alt?: string; caption?: string; preview?: string | null }>;
  /** panel entries with a transient preview link (stripped in toMap). */
  panels?: EditablePanel[];
}

/**
 * RETIRED BLOCKS (2026-09-09) — removed from this dropdown so they cannot be
 * added to new pages, but deliberately still rendered by picaluna-v3:
 *
 *   phone-cta            a text section with a tel: link does the same job
 *   testimonials         \
 *   directors-directory   > superseded / no longer offered to authors
 *   charity-directory    /  (charity-partners renders the same records)
 *
 * They stay in picaluna-v3's SECTION_COMPONENTS enum on purpose: /faq,
 * /contact, /reviews and /about/team still carry these sections and must keep
 * rendering. Dropping them from the site enum would fail those pages' Zod
 * validation and break the build. Removing a value HERE only stops new use.
 */
/**
 * RETIRED, still on the wire: 'faq-hub' (#202). It rendered the whole
 * categorised FAQ bank, which is now Page FAQs with Display type = Full page.
 * Dropped from this list so nobody picks it again, but the value must stay
 * acceptable everywhere downstream — picaluna-v3's zod enum aborts the entire
 * build on a stored value it does not know. Sections still holding it are
 * treated as full-page FAQs.
 */
export const COMPONENT_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Blog articles list', value: 'blog-list' },
  { label: 'Ceremony RSVP search', value: 'rsvp-search' },
  { label: 'Charity partners', value: 'charity-partners' },
  { label: 'Contact form', value: 'contact-us' },
  { label: 'Downloads (brochures & documents)', value: 'downloads' },
  { label: 'Funeral Costs', value: 'funeralCosts' },
  { label: 'Funeral directors grid', value: 'directors-grid' },
  { label: 'Guides list', value: 'guides-list' },
  { label: 'Page FAQs', value: 'faq-overview' },
  { label: 'Panel grid', value: 'panels' },
  { label: 'Photo gallery', value: 'gallery' },
  { label: 'Regions overview', value: 'regions-overview' },
  { label: 'Service cards', value: 'service-cards' },
  { label: 'Venues directory', value: 'venues-directory' },
  { label: 'What happens next (4 steps)', value: 'what-happens-next' },
  { label: 'YouTube video', value: 'youtube' },
  // SERVICES-CMS blocks — each renders from a Website services record (the
  // `service` id below the dropdown), so data and page can never disagree.
  { label: 'Service FAQs', value: 'service-faqs' },
  { label: 'Service hero (price + schema)', value: 'service-hero' },
  { label: "Service inclusions (what's included)", value: 'service-inclusions' },
  // Region-page composition — each scopes itself from the page's address,
  // or from its own state/region/suburb fields.
  { label: 'Regions in a state', value: 'regions-list' },
  { label: 'Suburbs in a region', value: 'suburbs-list' },
  { label: 'Venues in an area', value: 'venues-list' },
  { label: 'Places near this suburb', value: 'suburb-places' },
  // Sub-regions (#260): offered only once picaluna-v3 accepts both keys (SUBREGION_BLOCKS_LIVE, suburb-page.ts).
  ...(SUBREGION_BLOCKS_LIVE
    ? [
        { label: 'Sub-regions in a region', value: 'subregions-list' },
        { label: 'Sub-regions this suburb is part of', value: 'suburb-subregions' },
      ]
    : []),
  // The suburb page template's data blocks (#262): offered only once picaluna-v3 accepts both keys.
  ...(SUBURB_TEMPLATE_BLOCKS_LIVE
    ? [
        { label: 'Arrangers who work in this suburb', value: 'suburb-arrangers' },
        { label: 'Families helped in this suburb', value: 'suburb-families' },
      ]
    : []),
];

/**
 * The dropdown's grouped view (#200). 23 blocks in one unsorted list was a
 * scroll-and-hunt; alphabetical within a group plus the filter box means you
 * can type "photo" and be done.
 *
 * Grouped rather than one flat A–Z because the last two groups are not
 * arbitrary: the SERVICES-CMS blocks all render from a services record, and the
 * region-composition blocks all scope themselves from the page address. Flat
 * sorting scatters them among blocks that behave nothing like them.
 *
 * Derived from COMPONENT_OPTIONS rather than re-listing the blocks, so adding
 * one to that array is still the only edit needed — it lands in "Page blocks"
 * unless its value is named below.
 */
const SERVICE_BLOCK_VALUES = new Set(['service-hero', 'service-inclusions', 'service-faqs']);
const AREA_BLOCK_VALUES = new Set([
  'regions-list',
  'suburbs-list',
  'venues-list',
  'suburb-places',
  'subregions-list',
  'suburb-subregions',
  'suburb-arrangers',
  'suburb-families',
]);

export const COMPONENT_GROUPS: Array<{ label: string; items: Array<{ label: string; value: string }> }> = [
  {
    label: 'Page blocks',
    items: [
      /* "Text only" is a real, choosable option, not just the placeholder.
         It was only ever the dropdown's placeholder text, so an author who
         picked a block by mistake could get back to plain copy solely via
         the small clear (x) — a control nobody looks for, on a list where
         every other choice is made by picking a row.

         Its value is '' rather than a name, because a text-only section is
         one with NO component: toMap's put() skips empty strings, so this
         stores exactly what an unset dropdown always stored. Nothing about
         the saved shape changes. */
      { label: 'Text only', value: '' },
      ...COMPONENT_OPTIONS.filter((o) => !SERVICE_BLOCK_VALUES.has(o.value) && !AREA_BLOCK_VALUES.has(o.value)),
    ],
  },
  {
    label: 'Service pages (from a services record)',
    items: COMPONENT_OPTIONS.filter((o) => SERVICE_BLOCK_VALUES.has(o.value)),
  },
  {
    label: 'Region & suburb pages',
    items: COMPONENT_OPTIONS.filter((o) => AREA_BLOCK_VALUES.has(o.value)),
  },
];

/**
 * Components that ignore the heading and copy entirely — they render a fixed
 * block of their own.
 *
 * NOT the same set as the website's SELF_CONTAINED, and the difference matters:
 * rsvp-search and contact-us own their <section> wrapper but DO render the
 * heading and copy passed to them (live pages set both), so hiding those
 * fields made the only text a visitor reads uneditable.
 */
// venues-directory came OUT of this set (#232): it now renders an author
// heading + copy above the per-state groups, so hiding the fields left the
// block's only editable text unreachable.
const IGNORES_HEADING_AND_COPY = new Set(['guides-list']);

/**
 * Components whose copy reaches the page as PLAIN TEXT, not Markdown — the
 * site interpolates it rather than rendering it, so offering a Markdown editor
 * would promise formatting that ships as literal asterisks.
 */
const PLAIN_TEXT_COPY = new Set(['rsvp-search', 'contact-us', 'faq-overview', 'service-hero']);

/**
 * Blocks that render everything from their service RECORD — the heading is an
 * optional override and copy is not read at all, so the Markdown editor would
 * promise text that never ships.
 */
const NO_COPY = new Set(['service-inclusions', 'service-faqs', 'youtube']);

const RENDERS_WITHOUT_HEADING = new Set([
  // The content IS the block; a heading above it is the author's choice
  // (#194/#195). Not the same as self-contained — these still render inside
  // the standard <Section> wrapper, they just do not need its h2.
  'youtube',
  'gallery',
  'rsvp-search',
  'contact-us',
  'faq-overview',
  'venues-directory',
  'guides-list',
  'faq-hub',
  // heading is an optional override on all three (h1 for the hero, h2 for
  // the lists) — the block supplies its own from the service record.
  'service-hero',
  'service-inclusions',
  'service-faqs',
  // "Part of the Eastern Suburbs": a line of links, no heading of its own (#260).
  'suburb-subregions',
]);

@Component({
  selector: 'app-content-sections-editor',
  templateUrl: './content-sections-editor.component.html',
})
export class ContentSectionsEditorComponent {
  sections: EditableSection[] = [];
  componentOptions = COMPONENT_OPTIONS;
  /** Grouped + alphabetical view for the dropdown (#200), narrowed for a template (#262). */
  componentGroups = COMPONENT_GROUPS;
  /** "Fills from" on a template's text block (#262). */
  readonly bindOptions = BIND_OPTIONS;

  /** What is being edited (#262): a page, a suburb page template, or one block inserted on a suburb's page. */
  @Input() set mode(m: SectionsEditorMode) {
    this.editorMode = m;
    this.componentGroups = componentGroupsFor(m, COMPONENT_GROUPS);
  }
  get mode(): SectionsEditorMode {
    return this.editorMode;
  }
  private editorMode: SectionsEditorMode = 'page';

  /** Something may have changed (#263). The preview debounces it and compares toMap(), so extra emits cost nothing. */
  @Output() edited = new EventEmitter<void>();
  /** The block being worked on: last focused or clicked (its card's data-section-id). */
  activeSectionId: string | null = null;
  @Output() activeChange = new EventEmitter<string | null>();

  @HostListener('input')
  @HostListener('change')
  @HostListener('keyup')
  onEdit(): void {
    this.edited.emit();
  }

  @HostListener('focusin', ['$event.target'])
  @HostListener('click', ['$event.target'])
  onTouch(target: EventTarget | null): void {
    const id = (target as Element | null)?.closest?.('[data-section-id]')?.getAttribute('data-section-id');
    if (id && id !== this.activeSectionId) {
      this.activeSectionId = id;
      this.activeChange.emit(id);
    }
    this.edited.emit();
  }

  @HostListener('document:click', ['$event.target'])
  onOverlayClick(target: EventTarget | null): void {
    if ((target as Element | null)?.closest?.(OVERLAY_PANELS)) this.edited.emit();
  }
  // The fixed choices: content-sections-options.ts, and the brand palette (shared with every Panel grid panel, #264)
  // in block-colours.ts.
  readonly imageLayoutOptions = IMAGE_LAYOUT_OPTIONS;
  /** The hero's headline and buttons alignment, both Left by default; a block's are in BlockLookComponent. */
  readonly alignOptions = ALIGN_OPTIONS;
  readonly variantOptions = VARIANT_OPTIONS;
  readonly backgroundOptions = BACKGROUND_OPTIONS;
  readonly textColourOptions = TEXT_COLOUR_OPTIONS;
  readonly linkStyleOptions = LINK_STYLE_OPTIONS;
  readonly suburbPlacesOptions = SUBURB_PLACES_OPTIONS;
  readonly faqVariantOptions = FAQ_VARIANT_OPTIONS;
  readonly blogVariantOptions = BLOG_VARIANT_OPTIONS;
  readonly galleryVariantOptions = GALLERY_VARIANT_OPTIONS;
  readonly panelColumnOptions = PANEL_COLUMN_OPTIONS;
  readonly wideColumnOptions = WIDE_COLUMN_OPTIONS;
  readonly panelRowOptions = PANEL_ROW_OPTIONS;

  /** A panel's own colour select names what it inherits while blank (#264). */
  panelBackgroundPlaceholder(s: EditableSection): string {
    return inheritedPlaceholder(this.backgroundOptions, s.panelBackground, 'None (plain card)');
  }

  panelTextColourPlaceholder(s: EditableSection): string {
    return inheritedPlaceholder(this.textColourOptions, s.panelTextColour, 'Automatic');
  }

  /** Load from a page record's stored sections (map or array shape). */
  @Input() set initial(raw: CmsPage['sections'] | undefined) {
    this.initFrom(raw);
  }

  constructor(
    private modalService: ModalService,
    private fileService: FileService,
    private cdr: ChangeDetectorRef,
    private accountService: AccountService
  ) {
    // Funeral-cost pricing is scoped per region, and a typo in a free-text field
    // silently shows the WRONG prices — so this one is a picker built from
    // the live Regions list, not an input.
    // ensureFullAccount() first: account$ replays a content-STRIPPED account
    // (the server omits the content subtrees from ordinary GETs), so taking
    // first() off it can hand back an account with no regions, suburbs or
    // FAQs at all — and an empty picker is indistinguishable from "none
    // exist". The page list happens to load content before opening this
    // editor, but relying on someone else's call is how #199 shipped a
    // picker that warned against every correct address.
    this.accountService
      .ensureFullAccount()
      .pipe(
        switchMap(() => this.accountService.account$),
        first()
      )
      .subscribe({
        next: (a: any) => {
          const raw = a?.data?.content?.regions;
          const list = (Array.isArray(raw) ? raw : Object.values(raw || {})).filter(
            (r: any) => r && typeof r === 'object' && typeof r.slug === 'string' && r.slug
          );
          const regionChoices = list
            .map((r: any) => ({ label: r.name || r.slug, value: r.slug }))
            .sort((x: any, y: any) => x.label.localeCompare(y.label));

          this.packageRegionOptions = [
            { label: 'Global (mainline prices)', value: '' },
            ...regionChoices,
          ];

          // Scope pickers for the area blocks. A blank value keeps the existing
          // behaviour — derive the scope from the page's own address — which is
          // right on a region or suburb page and wrong nowhere, so it stays the
          // default rather than forcing a choice.
          this.scopeRegionOptions = [
            { label: 'Automatic (from the page address)', value: '' },
            ...regionChoices,
          ];
          this.scopeStateOptions = [
            { label: 'Automatic (from the page address)', value: '' },
            ...Array.from(
              new Set(list.map((r: any) => String(r.state || 'NSW').toUpperCase()))
            )
              .sort()
              .map((st: any) => ({ label: st, value: st })),
          ];

          const rawSuburbs = a?.data?.content?.suburbs;
          const regionById = new Map(list.map((r: any) => [r?._meta?.id, r]));
          this.scopeSuburbOptions = [
            { label: 'Automatic (from the page address)', value: '' },
            ...(Array.isArray(rawSuburbs) ? rawSuburbs : Object.values(rawSuburbs || {}))
              .filter((sb: any) => sb && typeof sb === 'object' && sb.slug)
              .map((sb: any) => {
                const r: any = regionById.get(sb.regionId);
                return {
                  label: r ? `${sb.name || sb.slug} (${r.name || r.slug})` : sb.name || sb.slug,
                  value: sb.slug,
                };
              })
              .sort((x: any, y: any) => x.label.localeCompare(y.label)),
          ];

          // The FAQ bank for the Page FAQs picker (#202): faq-options.ts.
          this.faqOptions = faqOptionsFrom(a?.data?.content);

          // "Costs shown" on a Funeral Costs block: each cost's NAME once, whatever region carries it.
          const rawCosts = a?.data?.funeralCosts;
          this.costOptions = Array.from(
            new Set(
              (Array.isArray(rawCosts) ? rawCosts : Object.values(rawCosts || {}))
                .filter((c: any) => c && typeof c === 'object' && c.isActive !== false && typeof c.name === 'string' && c.name.trim())
                .map((c: any) => c.name.trim() as string)
            )
          )
            .sort((x, y) => x.localeCompare(y))
            .map((name) => ({ label: name, value: name }));
        },
        error: () => {},
      });
  }

  /**
   * The FAQ bank for the Page FAQs picker (#202) — question as the label,
   * stable record id as the value. Empty until the account loads.
   */
  faqOptions: Array<{ label: string; value: string }> = [];

  /** The cost names a Funeral Costs block can show. Populated from the account. */
  costOptions: Array<{ label: string; value: string }> = [];

  /** Region choices for the Funeral Costs block. Populated from the account. */
  packageRegionOptions: Array<{ label: string; value: string }> = [
    { label: 'Global (mainline prices)', value: '' },
  ];
  /** Scope choices for the area blocks — blank means derive from the address. */
  scopeRegionOptions: Array<{ label: string; value: string }> = [
    { label: 'Automatic (from the page address)', value: '' },
  ];
  scopeStateOptions: Array<{ label: string; value: string }> = [
    { label: 'Automatic (from the page address)', value: '' },
  ];
  scopeSuburbOptions: Array<{ label: string; value: string }> = [
    { label: 'Automatic (from the page address)', value: '' },
  ];

  /** The Funeral Costs block picks its pricing region explicitly. */
  showFuneralCostRegion(s: EditableSection): boolean {
    return s.component === 'funeralCosts';
  }

  initFrom(raw: CmsPage['sections'] | CmsPageSection[] | undefined): void {
    const list: CmsPageSection[] = !raw
      ? []
      : Array.isArray(raw)
        ? (raw as CmsPageSection[]).filter(Boolean)
        : Object.values(raw).filter(Boolean);
    this.sections = list
      .slice()
      .sort(
        (a, b) =>
          (a?._meta?.index ?? Number.MAX_SAFE_INTEGER) - (b?._meta?.index ?? Number.MAX_SAFE_INTEGER)
      )
      .map((s) => {
        const { _meta, ...fields } = s;
        const editable: EditableSection = {
          id: _meta?.id || `id-s${this.shortId()}`,
          ...fields,
          servicesCsv: (fields.services ?? []).join(', '),
          // Closed on load: with many blocks, the collapsed headers ARE the
          // page outline. A newly added card opens (addSection).
          collapsed: true,
        };
        // Lift a stored singular `link` into `links` (#230) so the author
        // sees ONE list rather than two competing fields. Only when `links`
        // is absent — if both exist the array is the newer authored value
        // and the stale singular must not be re-appended on every open.
        // toMap then writes `links` and drops `link`, so this converts on
        // first save without needing a migration.
        if (!editable.links?.length && editable.link?.label && editable.link?.target) {
          editable.links = [{ ...editable.link, style: 'link' }];
        }
        // A list the store hands back id-keyed reads as a list.
        if (editable.costs && !Array.isArray(editable.costs)) editable.costs = Object.values(editable.costs);
        if (typeof editable.heroFileId === 'number') {
          this.fileService.getPreviewLink(String(editable.heroFileId)).subscribe({
            next: (url) => (editable.heroPreview = url),
            error: () => {},
          });
        }
        for (const img of editable.images ?? []) {
          if (typeof img.fileId !== 'number') continue;
          this.fileService.getPreviewLink(String(img.fileId)).subscribe({
            next: (url) => (img.preview = url),
            error: () => {},
          });
        }
        for (const pn of editable.panels ?? []) {
          if (typeof pn.imageFileId !== 'number') continue;
          this.fileService.getPreviewLink(String(pn.imageFileId)).subscribe({
            next: (url) => (pn.preview = url),
            error: () => {},
          });
        }
        return editable;
      });
  }

  // ── Section list operations ─────────────────────────────────────────────

  /**
   * One hero per page — each renders the page headline (h1). service-hero
   * counts too: it carries the hero presentation on a section-type block.
   * Drives the host action bars' "Add hero" disabled state; validate() stays
   * as the backstop for data that arrives with two.
   */
  get hasHero(): boolean {
    return this.sections.some(
      (s) => s.type === 'editorial-hero' || s.component === 'service-hero'
    );
  }

  addSection(type: 'editorial-hero' | 'section'): void {
    if (type === 'editorial-hero' && this.hasHero) return;
    const seed: EditableSection =
      type === 'editorial-hero'
        ? { id: `id-s${this.shortId()}`, type, heading: '', copy: '', buttons: [], collapsed: false }
        : { id: `id-s${this.shortId()}`, type, heading: '', collapsed: false };
    // Heroes go first (that is where the site renders them); sections append.
    if (type === 'editorial-hero') this.sections.unshift(seed);
    else this.sections.push(seed);
    // Bring the new card to the person, instead of making them scroll to it —
    // the Add buttons live in the host's sticky footer, so the new card can
    // be a whole page-height away.
    setTimeout(() => {
      const cards = document.querySelectorAll('.section-card');
      const target = type === 'editorial-hero' ? cards[0] : cards[cards.length - 1];
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /**
   * Explicit change-detection nudge for the building-block dropdown. The
   * conditional field rows (limit, panels, service id, …) are driven by
   * template method calls; a PrimeNG dropdown overlay appended to <body>
   * inside a DynamicDialog has been seen to update the model without the
   * dialog re-rendering until the next interaction — so the row for the
   * newly chosen block never appeared. Forcing a pass on every change makes
   * the switch immediate.
   */
  onComponentChanged(): void {
    this.cdr.detectChanges();
  }

  /** A template's hero stays first and can't be removed; an inserted block is deleted from its outline (#262). */
  canRemove(s: EditableSection): boolean {
    return isRemovable(this.mode, s);
  }

  canMoveSection(i: number, delta: number): boolean {
    return canMove(this.mode, this.sections, i, delta);
  }

  removeSection(i: number): void {
    const s = this.sections[i];
    const modal = this.modalService.generic({
      title: 'Remove section',
      copy: [`Remove ${s.heading ? `"${s.heading}"` : 'this section'} from the page?`],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Remove', key: 'confirm', class: 'p-button-danger' },
      ],
    });
    modal.onClose.subscribe({
      next: (key: string) => {
        if (key?.toString().toLowerCase() !== 'confirm') return;
        this.sections.splice(i, 1);
        // The removed block was the one being worked on: none is now (the preview shows the page, or its hint).
        if (s.id === this.activeSectionId) {
          this.activeSectionId = null;
          this.activeChange.emit(null);
        }
        this.edited.emit();
      },
    });
  }

  moveSection(i: number, delta: number): void {
    const j = i + delta;
    if (!this.canMoveSection(i, delta)) return;
    const [s] = this.sections.splice(i, 1);
    this.sections.splice(j, 0, s);
    this.edited.emit(); // the header's buttons stop their click reaching the host
  }

  // ── Per-section field helpers ───────────────────────────────────────────

  isSelfContained(s: EditableSection): boolean {
    return !!s.component && IGNORES_HEADING_AND_COPY.has(s.component);
  }

  /** True when the site renders this section's copy as plain text. */
  usesPlainCopy(s: EditableSection): boolean {
    return !!s.component && PLAIN_TEXT_COPY.has(s.component);
  }

  /**
   * The site only renders a section link through the standard wrapper (and
   * rsvp-search, which takes one explicitly). Offering the field elsewhere
   * invites someone to fill in a link that is silently discarded.
   */
  showLink(s: EditableSection): boolean {
    if (!s.component) return true;
    // Not needing a HEADING and not being able to carry a LINK are different
    // things; these blocks render their own footer link under the content.
    if (s.component === 'rsvp-search') return true;
    if (s.component === 'faq-overview') return true;
    return !RENDERS_WITHOUT_HEADING.has(s.component);
  }

  sectionSummary(s: EditableSection): string {
    if (s.type === 'editorial-hero') return 'Hero';
    const comp = COMPONENT_OPTIONS.find((o) => o.value === s.component)?.label;
    return comp || 'Text';
  }

  showVariant(s: EditableSection): boolean {
    return s.component === 'funeralCosts';
  }
  /** directors-grid only. The blog list had it too, alongside columns × rows
   *  and articles-per-page — three controls where two did nothing, and the
   *  "(overrides columns × rows)" label admitted as much without stopping the
   *  confusion (#201). The three live blog sections were migrated to
   *  columns × rows first; directors-grid keeps it because it has no grid
   *  control of its own. */
  showLimit(s: EditableSection): boolean {
    return s.component === 'directors-grid';
  }
  /** Page size belongs to the FULL blog variant — a teaser has no pager, and
   *  offering the field there is what let the home page end up with limit AND
   *  perPage both set to 3, one of which does nothing (#201). */
  showPerPage(s: EditableSection): boolean {
    return s.component === 'blog-list' && s.variant === 'full';
  }
  /** Columns apply to both blog variants; rows only caps a teaser. */
  showBlogColumns(s: EditableSection): boolean {
    return s.component === 'blog-list';
  }

  /** Directors grid / venues directory column count, 2–6 (#231, #232). */
  showWideColumns(s: EditableSection): boolean {
    return s.component === 'directors-grid' || s.component === 'venues-directory';
  }
  showBlogRows(s: EditableSection): boolean {
    return s.component === 'blog-list' && s.variant !== 'full';
  }
  showDirectorFlags(s: EditableSection): boolean {
    return s.component === 'directors-grid';
  }
  showPanels(s: EditableSection): boolean {
    return s.component === 'panels';
  }
  showServices(s: EditableSection): boolean {
    return s.component === 'service-cards';
  }
  showFaqs(s: EditableSection): boolean {
    return s.component === 'faq-overview' || s.component === 'faq-hub';
  }

  /** The FAQ picker is for Section blocks; Full page takes the toggled set. */
  showFaqPicker(s: EditableSection): boolean {
    return this.showFaqs(s) && !this.isFullPageFaqs(s) && this.mode !== 'template';
  }

  /**
   * faq-hub is the retired block (#202) — it always rendered the whole
   * categorised bank, which is what Full page does now. Its stored value
   * still loads, so it reads as full-page here rather than silently
   * behaving like a Section block.
   */
  isFullPageFaqs(s: EditableSection): boolean {
    if (s.component === 'faq-hub') return true;
    return s.variant === 'full';
  }
  /** Reduce a pasted YouTube address to its 11-char id, or null.
   *  MIRRORS picaluna-v3's src/lib/youtube.ts — if that parser changes, this
   *  one must too, or the editor accepts links the site then refuses to
   *  render. */
  private youTubeId(input: string): string | null {
    const raw = (input || '').trim();
    if (!raw) return null;
    const ID = /^[A-Za-z0-9_-]{11}$/;
    if (ID.test(raw)) return raw;
    let url: URL;
    try {
      url = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    } catch {
      return null;
    }
    const host = url.hostname.replace(/^www\./, '');
    const candidates: Array<string | null> = [];
    if (host === 'youtu.be') {
      candidates.push(url.pathname.slice(1));
    } else if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      candidates.push(url.searchParams.get('v'));
      const m = url.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
      if (m) candidates.push(m[1]);
    }
    for (const c of candidates) {
      if (c && ID.test(c)) return c;
    }
    return null;
  }

  /** The heading label, which must tell the truth per block: required for a
   *  plain section, optional where the block is the content (#194/#195).
   *  No longer says "h2": the level is the author's choice now (#259). */
  headingLabel(s: EditableSection): string {
    if (this.showSingleService(s)) return 'Heading (optional — the block has its own)';
    if (s.component && RENDERS_WITHOUT_HEADING.has(s.component)) {
      return 'Heading (optional — this block does not need one)';
    }
    if (!s.component) return 'Heading (optional if there is text below)';
    return 'Heading (required for it to show)';
  }

  /** The level select sits beside every block heading the site renders as a
   *  section heading. Not on service-hero: its heading is the page's h1. */
  showHeadingLevel(s: EditableSection): boolean {
    return !this.isSelfContained(s) && s.component !== 'service-hero';
  }
  /** Heading and button alignment: section headings only, not the hero's h1. */
  showAlign(s: EditableSection): boolean {
    return this.showHeadingLevel(s) && s.type !== 'editorial-hero';
  }

  showYouTube(s: EditableSection): boolean {
    return s.component === 'youtube';
  }
  /** The area-listing blocks take optional state/region/suburb scoping. */
  showAreaScope(s: EditableSection): boolean {
    return s.component === 'regions-list' || s.component === 'suburbs-list' || s.component === 'venues-list';
  }
  /** Blocks that take a REGION scope: the area lists plus the directors grid,
   *  which supported region scoping but had no field to set it. */
  showScopeRegion(s: EditableSection): boolean {
    return (
      s.component === 'suburbs-list' ||
      s.component === 'subregions-list' ||
      s.component === 'venues-list' ||
      s.component === 'directors-grid'
    );
  }
  /** The three service-* blocks each render ONE service record. */
  showSingleService(s: EditableSection): boolean {
    return s.component === 'service-hero' || s.component === 'service-inclusions' || s.component === 'service-faqs';
  }
  /** service-hero carries the hero presentation on a section-type block. */
  showHeroFields(s: EditableSection): boolean {
    return s.component === 'service-hero';
  }
  /** A template has no paragraphs (#262): each suburb's own text is on its record. */
  hidesCopy(s: EditableSection): boolean {
    return this.mode === 'template' || (!!s.component && NO_COPY.has(s.component));
  }
  /** The hero's lead: on a template it is each suburb's opening paragraph (#262). */
  showsLead(s: EditableSection): boolean {
    return s.type === 'editorial-hero' && this.mode !== 'template';
  }
  /** A template's text block may read the suburb's About text (#262). */
  showBind(s: EditableSection): boolean {
    return this.mode === 'template' && s.type === 'section' && !s.component;
  }
  /** On a template the FAQ block shows each suburb's own FAQs, chosen on its Page tab (#262). */
  readsSuburbFaqs(s: EditableSection): boolean {
    return this.mode === 'template' && this.showFaqs(s);
  }
  /** Placeholders ({{suburb:name}}, …) resolve on suburb pages only: templates and inserted blocks (#262). */
  get showsPlaceholders(): boolean {
    return this.mode !== 'page';
  }
  /** A placeholder from the menu, at the caret of `input` when given, else at the end. */
  withToken(text: string | null | undefined, token: string, input?: HTMLInputElement): string {
    return insertToken(text, token, input?.selectionStart, input?.selectionEnd).text;
  }
  /** The region page's address put first in a link target (the only placeholder a target may hold). */
  withTargetToken(target: string | null | undefined, token: string): string {
    return withTargetToken(target, token);
  }
  /** Plain/standard sections render `image` as a figure under the copy (not on a template: #262). */
  showSectionImage(s: EditableSection): boolean {
    if (s.type !== 'section' || this.mode === 'template') return false;
    if (!s.component) return true;
    return !this.isSelfContained(s) && !RENDERS_WITHOUT_HEADING.has(s.component);
  }

  /** The absolute ceiling — 4 columns × 3 rows, the largest grid offered.
   *  toMap still slices at THIS, never at the per-section cap: reducing the
   *  grid must not delete panels on the next save (#192). */
  readonly maxPanels = 12;

  /** What THIS section can hold: the selected columns × rows, or the ceiling
   *  when either is left on "auto" (#192). The site applies the same product —
   *  `Panels.astro` renders at most columns × rows — so the editor was letting
   *  authors add panels the page would never show. */
  panelCap(s: EditableSection): number {
    return s.columns && s.rows ? s.columns * s.rows : this.maxPanels;
  }

  /** Panels beyond this section's cap: authored, saved, but not rendered.
   *  Shown as a warning rather than deleted — a dropdown change must never
   *  destroy content (the comment on maxPanels records that happening once). */
  panelsOverCap(s: EditableSection): number {
    return Math.max(0, (s.panels ?? []).length - this.panelCap(s));
  }

  addPanel(s: EditableSection): void {
    if ((s.panels ?? []).length >= this.panelCap(s)) return;
    s.panels = [...(s.panels ?? []), { heading: '', copy: '' }];
  }
  removePanel(s: EditableSection, i: number): void {
    s.panels = (s.panels ?? []).filter((_p, k) => k !== i);
  }
  movePanel(s: EditableSection, i: number, dir: -1 | 1): void {
    const arr = [...(s.panels ?? [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    s.panels = arr;
  }
  ensurePanelLink(pn: { link?: { label: string; target: string } | null }): void {
    if (!pn.link) pn.link = { label: '', target: '' };
  }
  uploadPanelImage(pn: { imageFileId?: number | null; preview?: string | null }): void {
    const ref = this.modalService.fileUpload({
      title: 'Upload panel image',
      bucketPrefix: 'page-images',
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number | string; link?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file) return;
      pn.imageFileId = +file.id;
      pn.preview = file.link ?? null;
      this.cdr.detectChanges();
      this.edited.emit();
    });
  }
  removePanelImage(pn: { imageFileId?: number | null; preview?: string | null }): void {
    pn.imageFileId = null;
    pn.preview = null;
  }

  addFaq(s: EditableSection): void {
    s.faqs = [...(s.faqs ?? []), { question: '', answer: '' }];
  }
  removeFaq(s: EditableSection, i: number): void {
    s.faqs = (s.faqs ?? []).filter((_f, k) => k !== i);
  }

  addButton(s: EditableSection): void {
    if ((s.buttons ?? []).length >= 2) return;
    s.buttons = [...(s.buttons ?? []), { label: '', target: '' }];
  }
  removeButton(s: EditableSection, i: number): void {
    s.buttons = (s.buttons ?? []).filter((_b, k) => k !== i);
  }

  ensureLink(s: EditableSection): void {
    if (!s.link) s.link = { label: '', target: '' };
  }

  /**
   * Links/buttons under a block (#230). The array supersedes the singular
   * `link`; `initFrom` lifts a stored `link` into `links` so an author sees
   * one list rather than two competing fields.
   */
  addLink(s: EditableSection): void {
    if (!s.links) s.links = [];
    s.links.push({ label: '', target: '', style: 'link' });
  }

  removeLinkAt(s: EditableSection, i: number): void {
    s.links?.splice(i, 1);
  }

  /** Reorder: the site renders them in this order, left to right. */
  moveLink(s: EditableSection, i: number, delta: number): void {
    const list = s.links;
    if (!list) return;
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
  }

  /**
   * Seed the link text from the page the author just picked (#199) — but only
   * when it is still EMPTY. A typed label is the author's wording and outranks
   * the page title, which is written for the <title> tag, not for a button.
   *
   * Tolerates a null holder: the panel link is created by ensurePanelLink on
   * the same change event, and argument order between the two is not ours to
   * rely on.
   */
  applyPickedLabel(
    holder: { label?: string } | null | undefined,
    picked: { title?: string }
  ): void {
    if (!holder || !picked?.title) return;
    if ((holder.label || '').trim()) return;
    holder.label = picked.title;
  }
  removeLink(s: EditableSection): void {
    s.link = null;
  }

  showShowMax(s: EditableSection): boolean {
    return s.component === 'directors-grid';
  }
  showGalleryFields(s: EditableSection): boolean {
    return s.component === 'gallery';
  }
  /** Columns apply to the GRID gallery only — the carousel sizes its slides
   *  from the scroller, so offering the field there would promise a layout
   *  change that never happens. Blank variant means grid (the default). */
  showGalleryColumns(s: EditableSection): boolean {
    return s.component === 'gallery' && (s.variant ?? 'grid') === 'grid';
  }
  showDownloadFields(s: EditableSection): boolean {
    return s.component === 'downloads';
  }
  /** gallery/downloads carry their own uploads — the single section image
   *  would render UNDER them and read as a stray, so its controls hide. */
  hidesSectionImage(s: EditableSection): boolean {
    return s.component === 'gallery' || s.component === 'downloads';
  }

  addGalleryImages(s: EditableSection): void {
    const ref = this.modalService.fileUpload({
      title: 'Add gallery images',
      bucketPrefix: 'page-images',
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: true,
    });
    ref.onClose.subscribe((result?: { files: { id: number | string; link?: string }[] }) => {
      for (const file of result?.files ?? []) {
        s.images = [...(s.images ?? []), { fileId: +file.id, preview: file.link ?? null }];
      }
      this.cdr.detectChanges();
      this.edited.emit();
    });
  }
  removeGalleryImage(s: EditableSection, i: number): void {
    s.images = (s.images ?? []).filter((_x, k) => k !== i);
  }
  moveGalleryImage(s: EditableSection, i: number, dir: -1 | 1): void {
    const arr = [...(s.images ?? [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    s.images = arr;
  }

  addDownloadFiles(s: EditableSection): void {
    const ref = this.modalService.fileUpload({
      title: 'Add documents',
      bucketPrefix: 'page-downloads',
      accept: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      multiple: true,
    });
    ref.onClose.subscribe((result?: { files: { id: number | string; name?: string }[] }) => {
      for (const file of result?.files ?? []) {
        // Label prefills from the file name (minus extension) — usually right,
        // always editable.
        const label = (file.name ?? '').replace(/\.[^.]+$/, '').trim();
        s.files = [...(s.files ?? []), { fileId: +file.id, label, fileName: file.name }];
      }
      this.cdr.detectChanges();
      this.edited.emit();
    });
  }
  removeDownloadFile(s: EditableSection, i: number): void {
    s.files = (s.files ?? []).filter((_x, k) => k !== i);
  }
  moveDownloadFile(s: EditableSection, i: number, dir: -1 | 1): void {
    const arr = [...(s.files ?? [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    s.files = arr;
  }

  uploadSectionImage(s: EditableSection): void {
    const ref = this.modalService.fileUpload({
      title: 'Upload section image',
      bucketPrefix: 'page-images',
      // NOT image/* — the website only optimises raster formats, and an SVG or
      // GIF would upload cleanly here and then fail the site build.
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number; link?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file) return;
      s.heroFileId = file.id;
      if (file.link) s.heroPreview = file.link;
      this.edited.emit();
    });
  }

  removeSectionImage(s: EditableSection): void {
    s.heroFileId = null;
    s.heroPreview = null;
  }

  // ── Host API ────────────────────────────────────────────────────────────

  /**
   * Section-level validation (the host validates its own page-level fields).
   * `published` tightens the rules the way shipping does: a published layout
   * with no sections or no hero is a broken page, a draft is just a draft.
   */
  validate(published: boolean): string | null {
    // Two heroes = two h1s (editorial-hero AND service-hero both render one),
    // which breaks the one-h1 rule on every page it ships on.
    const heroCount = this.sections.filter(
      (s) => s.type === 'editorial-hero' || s.component === 'service-hero'
    ).length;
    if (heroCount > 1) {
      return 'This page has more than one hero (a hero section and/or a "Service hero" block) — a page can only have one, because each renders the page headline.';
    }
    if (published && this.sections.length > 0 && heroCount === 0) {
      return 'This page has no hero section, so it would ship without a page headline (h1) at all. Add a hero — without an image it renders as a simple text banner.';
    }
    if (published && !this.sections.length) {
      return 'This page has no sections, so publishing it would replace the current page with a blank one. Add a section, or keep it unpublished while you work on it.';
    }

    for (const s of this.sections) {
      // Only where the field is actually ON SCREEN for this component. A
      // section that once was a blog list keeps its limit in the record after
      // the component changes, and rejecting a value the user cannot see or
      // clear would make the page unsaveable with no way out.
      for (const key of ['limit', 'perPage'] as const) {
        const visible = key === 'limit' ? this.showLimit(s) : this.showPerPage(s);
        if (!visible) continue;
        const v = (s as any)[key];
        if (v === undefined || v === null || v === '') continue;
        const n = Number(v);
        if (!Number.isInteger(n) || n < 1) {
          return `"${key === 'limit' ? 'Show at most' : 'Articles per page'}" must be a whole number of 1 or more.`;
        }
      }

      // The service-* blocks render nothing without their record id — the
      // same silent disappearance the heading gate below guards against.
      if (this.showSingleService(s) && !(s.service || '').trim()) {
        const label = COMPONENT_OPTIONS.find((o) => o.value === s.component)?.label ?? s.component;
        return `The "${label}" block needs a service id (e.g. direct-cremation) — without one it renders nothing.`;
      }

      if (published && s.component === 'gallery' && !(s.images ?? []).length) {
        return `The "${s.heading || 'Photo gallery'}" gallery has no images — add some, or remove the section before publishing.`;
      }
      if (published && s.component === 'downloads' && !(s.files ?? []).length) {
        return `The "${s.heading || 'Downloads'}" downloads block has no documents — add some, or remove the section before publishing.`;
      }
      if (s.component === 'downloads' && (s.files ?? []).some((f) => !(f.label || '').trim())) {
        return 'Every document in a downloads block needs a label — that is the text people click.';
      }

      // Mirrors what YouTube.astro DOES, not what this form shows: it renders
      // nothing at all when it cannot read an 11-char id, so a typo'd link is a
      // section that silently vanishes from the page.
      if (s.component === 'youtube') {
        const raw = (s.video || '').trim();
        if (!raw) {
          return 'The YouTube block has no video — paste the link from YouTube, or remove the section before publishing.';
        }
        if (!this.youTubeId(raw)) {
          return `"${raw}" is not a YouTube link this can read. Paste the address from YouTube's address bar or its Share button.`;
        }
      }

      // A section the site renders through the standard wrapper is gated on
      // its heading — without one it renders nothing at all, the same silent
      // disappearance as an imageless hero.
      // `!!s.component &&` used to lead this: a section with NO component is
      // the plain text block, so it could never be exempt whatever the set
      // said — the reason #195 was a different job from #194. A text section
      // with copy and no heading is legitimate (a paragraph continuing the
      // section above it), so the rule is now about having SOMETHING to show,
      // which is what SectionRenderer itself checks.
      const rendersHeadingless = s.component
        ? RENDERS_WITHOUT_HEADING.has(s.component)
        : !!(s.copy || '').trim();
      if (s.type === 'section' && !rendersHeadingless && !(s.heading || '').trim()) {
        return 'This section has no heading and nothing to show — add a heading, some text, or a building block.';
      }
    }
    return null;
  }

  /** Serialize back to the stored id-keyed `_meta.index` map. */
  toMap(): Record<string, CmsPageSection> {
    const sections: Record<string, CmsPageSection> = {};
    this.sections.forEach((s, i) => {
      const { id, heroPreview, servicesCsv, collapsed, ...fields } = s;
      const clean: any = { _meta: { id, index: i * 10 }, type: fields.type };

      const put = (key: string, v: any) => {
        if (v === undefined || v === null || v === '') return;
        clean[key] = v;
      };
      put('eyebrow', fields.eyebrow?.trim());
      put('heading', fields.heading?.trim());
      // Ungated, like the presentation fields below: switching a block to
      // preview another and back must not drop the author's choice. h2 is
      // the default and stores nothing, so an untouched block stays as it was.
      put('headingLevel', fields.headingLevel === 'h2' ? undefined : fields.headingLevel);
      put('align', fields.align === 'left' ? undefined : fields.align);
      // Robin, 2026-10-29. Ungated like the rest of the presentation fields. Each default stores nothing: a blank
      // heading colour follows the Text colour, the divider is on, the spacing is top and bottom. A buttons choice is
      // stored as made, Centre included: blank means "as the heading", not centre.
      put('headingColour', fields.headingColour);
      put('buttonAlign', fields.buttonAlign);
      if (fields.divider === false) clean.divider = false;
      put('spacing', fields.spacing === 'both' ? undefined : fields.spacing);
      const costs = (fields.costs ?? []).filter((c) => typeof c === 'string' && c.trim());
      if (costs.length) clean.costs = costs;
      put('copy', fields.copy?.trim());
      put('image', fields.image);
      if (typeof fields.heroFileId === 'number') clean.heroFileId = fields.heroFileId;
      put('imageAlt', fields.imageAlt?.trim());
      put('imageLayout', fields.imageLayout);
      put('anchor', fields.anchor?.trim());
      put('component', fields.component);
      put('service', fields.service?.trim());
      put('state', fields.state?.trim());
      put('region', fields.region?.trim());
      put('suburb', fields.suburb?.trim());
      // NOT gated on showVariant(): that returns true only for funeralCosts, so a
      // gallery's grid/carousel choice was discarded on every save. Same
      // reversibility rule as panels/faqs below — the site ignores fields its
      // component doesn't read.
      put('variant', fields.variant);
      put('video', fields.video);
      put('caption', fields.caption);
      if (this.showLimit(s) && typeof fields.limit === 'number') clean.limit = fields.limit;
      // NOT gated on showPerPage(): that is now false for a teaser, so merely
      // opening the /blog section in teaser mode and saving would have DROPPED
      // its page size — the same reversibility rule as panels/faqs above. The
      // site only reads perPage on the full variant, so carrying it costs
      // nothing and switching back restores the hub.
      if (s.component === 'blog-list' && typeof fields.perPage === 'number') clean.perPage = fields.perPage;
      if (this.showDirectorFlags(s)) {
        if (typeof fields.shuffle === 'boolean') clean.shuffle = fields.shuffle;
        if (typeof fields.photosOnly === 'boolean') clean.photosOnly = fields.photosOnly;
      }
      if (this.showShowMax(s) && typeof fields.showMax === 'number') clean.showMax = fields.showMax;
      // NOT gated on showPanels(): columns/rows now drive the gallery grid
      // (#196) and the blog teaser (#201) too, and gating on one component
      // silently DISCARDED the other two on save — the field was on screen,
      // the author set it, and it never reached the record. Same reversibility
      // rule as panels/faqs/perPage: the site ignores fields its component
      // does not read, so carrying them costs nothing.
      if (typeof fields.columns === 'number') clean.columns = fields.columns;
      if (typeof fields.rows === 'number') clean.rows = fields.rows;
      // Presentation fields (#226/#227/#228). Ungated for the same
      // reversibility reason as variant/columns above: these apply to EVERY
      // block, so gating on a component would silently discard an author's
      // choice the moment they previewed a different block and saved.
      // 'left' is the default and stores nothing — an absent value and an
      // explicit "left" render identically, so writing it is noise.
      put('background', fields.background);
      put('textColour', fields.textColour);
      // A template block's field (#262). Written whatever the block, as above: the site ignores it elsewhere.
      put('bind', fields.bind);
      // Panel grid colours (#264): same rule, so previewing another block
      // and switching back keeps them. Blank stores nothing.
      put('panelBackground', fields.panelBackground);
      put('panelTextColour', fields.panelTextColour);
      // Same rule, same reason: not gated on showFaqs(). Switching a block to
      // preview another and back must not drop the author's chosen FAQs.
      const faqIds = (fields.faqIds ?? []).filter((id) => typeof id === 'string' && id);
      if (faqIds.length) clean.faqIds = faqIds;
      if (typeof fields.groupByCategory === 'boolean') {
        clean.groupByCategory = fields.groupByCategory;
      }
      // Kept regardless of the current component (same reversibility rule as
      // panels/faqs below); preview links are transient and stripped.
      const images = (fields.images ?? [])
        .filter((im) => typeof im.fileId === 'number')
        .map(({ fileId, alt, caption }) => ({
          fileId,
          ...(alt?.trim() ? { alt: alt.trim() } : {}),
          ...(caption?.trim() ? { caption: caption.trim() } : {}),
        }));
      if (images.length) clean.images = images;
      const files = (fields.files ?? [])
        .filter((f) => typeof f.fileId === 'number')
        .map(({ fileId, label, fileName }) => ({
          fileId,
          ...(label?.trim() ? { label: label.trim() } : {}),
          ...(fileName ? { fileName } : {}),
        }));
      if (files.length) clean.files = files;
      const buttons = (fields.buttons ?? []).filter((b) => b.label?.trim() && b.target?.trim());
      if (buttons.length) clean.buttons = buttons.slice(0, 2);
      // Links/buttons (#230). `links` supersedes the singular `link`:
      // initFrom lifts a stored `link` into the array, so writing the array
      // and dropping `link` converts a section on its first save — no
      // migration needed. `link` is only carried when the array is empty,
      // which happens when the author deleted every row.
      const links = (fields.links ?? [])
        .filter((l) => l.label?.trim() && l.target?.trim())
        .map((l) => ({
          label: l.label.trim(),
          target: l.target.trim(),
          ...(l.style && l.style !== 'link' ? { style: l.style } : {}),
        }));
      if (links.length) {
        clean.links = links;
      } else if (fields.link?.label?.trim() && fields.link?.target?.trim()) {
        clean.link = fields.link;
      }
      // Content is kept whether or not its editor is currently on screen.
      // Gating on show*() meant switching a section's building block — to
      // preview an alternative, say — DELETED its panels, service ids or FAQs
      // on the next save, with no warning and no undo. The site ignores fields
      // its component doesn't read, so carrying them costs nothing and makes
      // the switch reversible.
      // Heading-gated only: a panel may be image+link, copy+link, or all.
      const panels = (fields.panels ?? [])
        .filter((p) => p.heading?.trim())
        .slice(0, this.maxPanels)
        .map(toStoredPanel);
      if (panels.length) clean.panels = panels;
      const services = (servicesCsv ?? '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      if (services.length) clean.services = services;
      const faqs = (fields.faqs ?? []).filter((f) => f.question?.trim() && f.answer?.trim());
      if (faqs.length) clean.faqs = faqs;

      sections[id] = clean as CmsPageSection;
    });
    return sections;
  }

  private shortId(): string {
    return Math.random().toString(36).slice(2, 8);
  }
}
